import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { STATUS_COLORS, STATUS_LABELS } from "@/lib/utils";
import { cn } from "@/lib/utils";

export function PageHero({
  title,
  subtitle,
  breadcrumbs,
  actions,
  compact,
}: {
  title: string;
  subtitle?: string;
  breadcrumbs?: { label: string; to?: string }[];
  actions?: ReactNode;
  compact?: boolean;
}) {
  return (
    <div className={cn("relative -mx-4 md:-mx-6", compact ? "mb-6" : "mb-6")}>
      <div
        className={cn(
          "bg-gradient-to-br from-argon-header via-[#1e2746] to-[#111827] px-4 md:px-6 pt-6 rounded-b-3xl shadow-lg",
          compact ? "pb-6" : "pb-24 md:pb-28",
        )}
      >
        {breadcrumbs && breadcrumbs.length > 0 && (
          <nav className="flex items-center gap-2 text-sm text-white/60 mb-3">
            {breadcrumbs.map((crumb, i) => (
              <span key={crumb.label} className="flex items-center gap-2">
                {i > 0 && <span>/</span>}
                {crumb.to ? (
                  <Link to={crumb.to} className="hover:text-white transition-colors">
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="text-white font-semibold">{crumb.label}</span>
                )}
              </span>
            ))}
          </nav>
        )}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">{title}</h1>
            {subtitle && <p className="text-sm text-white/70 mt-1 max-w-2xl">{subtitle}</p>}
          </div>
          {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
        </div>
      </div>
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}) {
  return (
    <PageHero
      title={title}
      subtitle={subtitle}
      breadcrumbs={[{ label: "Pages", to: "/" }, { label: title }]}
      actions={actions}
      compact
    />
  );
}

export function LoadingState() {
  return (
    <div className="flex items-center justify-center p-16">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-argon-primary border-t-transparent" />
    </div>
  );
}

export function ErrorState({ message }: { message: string }) {
  return (
    <div className="rounded-2xl bg-red-50 border border-red-100 p-8 text-center text-argon-danger font-medium">
      {message}
    </div>
  );
}

export function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-block px-2.5 py-1 rounded-md text-xs font-bold uppercase tracking-wide ${STATUS_COLORS[status] ?? "bg-gray-100 text-gray-700"}`}
    >
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}
